import { ArrowUpRight, Globe, Compass, Search } from "lucide-react";
import { Reveal, Chapter } from "@/components/Reveal";
import { scrollToSection } from "@/lib/scroll";

const SERVICES = [
  ["Core website", "Pages, responsive build, launch", "Included"],
  ["Domain + hosting", "Set up with you, at cost", "At cost"],
  ["Content support", "A little help finding the words", "Optional"],
  ["Aftercare", "Small edits kept free", "Included"],
];

const FEATURES = [
  {
    icon: Search,
    title: "Ready to be found",
    desc: "Responsive, fast and easy to use, with the details that make a small business look established.",
  },
  {
    icon: Compass,
    title: "A look that fits",
    desc: "A visual direction made around you — not a theme everyone has already seen.",
  },
];

const BrowserMock = () => (
  <div
    data-testid="work-showcase-mock"
    className="group overflow-hidden rounded-[1.5rem] border border-white/10 bg-[#181624] shadow-2xl shadow-black/30"
  >
    <div className="flex items-center justify-between border-b border-white/10 px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
      <span className="flex items-center gap-2">
        <span className="h-2.5 w-2.5 rounded-full bg-[#7650c7]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#f4d35e]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#7abcb0]" />
      </span>
      <span>elavate / live preview</span>
      <Globe size={13} aria-hidden />
    </div>
    <div className="relative overflow-hidden bg-[#f3f0e8] p-8 transition-transform duration-700 group-hover:scale-[1.015] sm:p-12">
      <div aria-hidden className="absolute -right-14 -top-14 h-48 w-48 rounded-full bg-[#7abcb0]/25" />
      <div aria-hidden className="absolute -bottom-20 -left-10 h-56 w-56 rounded-full border border-[#201e32]/10" />
      <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#6d687c]">
        Botanical studio · est. 2021
      </p>
      <p className="mt-5 font-display text-4xl font-bold leading-[0.95] tracking-[-0.04em] text-[#201e32] sm:text-5xl">
        Marlow
        <br />
        &amp; Moss<span className="text-[#7abcb0]">.</span>
      </p>
      <p className="mt-4 max-w-[280px] text-sm leading-relaxed text-[#6d687c]">
        Planting schemes and quiet green corners for homes that want to breathe.
      </p>
      <div className="mt-8 flex items-center gap-3">
        <span className="rounded-full bg-[#201e32] px-4 py-2 text-xs font-semibold text-[#f3f0e8]">
          Book a visit
        </span>
        <span className="rounded-full border border-[#201e32]/15 px-4 py-2 text-xs font-semibold text-[#201e32]/70">
          The garden diary
        </span>
      </div>
    </div>
  </div>
);

export const Services = () => (
  <section id="services" data-testid="services-section" className="px-5 py-24 sm:px-8 sm:py-32 lg:px-10">
    <div className="mx-auto max-w-7xl">
      <Chapter n="02" label="What we do" />

      <div className="mt-10 grid gap-10 lg:grid-cols-12">
        <Reveal className="lg:col-span-7">
          <h2 className="font-display text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-6xl">
            Websites with
            <br />
            somewhere to <span className="text-[#7650c7]">go.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15} className="flex items-end lg:col-span-5">
          <p className="max-w-[460px] text-base leading-relaxed text-[#6d687c] sm:text-lg">
            The right amount of design, development and practical support to get your next
            chapter moving.
          </p>
        </Reveal>
      </div>

      <div className="mt-16 grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <Reveal>
            <div className="overflow-hidden rounded-[1.5rem] border border-[#201e32]/10 bg-white">
              {SERVICES.map(([name, desc, tag], i) => (
                <div
                  key={name}
                  data-testid={`service-row-${i + 1}`}
                  className="group flex items-center justify-between gap-4 border-b border-[#201e32]/10 px-6 py-6 transition-colors duration-300 last:border-0 hover:bg-[#f3f0e8] sm:px-8"
                >
                  <div>
                    <p className="font-display text-lg font-bold tracking-[-0.02em] sm:text-xl">{name}</p>
                    <p className="mt-1 text-xs text-[#6d687c] sm:text-sm">{desc}</p>
                  </div>
                  <span
                    className={`shrink-0 rounded-full px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] ${
                      tag === "Included"
                        ? "bg-[#7650c7]/10 text-[#7650c7]"
                        : tag === "At cost"
                          ? "bg-[#7abcb0]/15 text-[#2f6d62]"
                          : "bg-[#f4d35e]/40 text-[#201e32]/70"
                    }`}
                  >
                    {tag}
                  </span>
                </div>
              ))}
            </div>
          </Reveal>

          <div className="mt-5 grid gap-5 sm:grid-cols-2">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={0.1 + i * 0.12}>
                <div
                  data-testid={`service-feature-${i + 1}`}
                  className="flex h-full flex-col rounded-[1.5rem] bg-[#201e32] p-7 text-[#f3f0e8] transition-transform duration-300 hover:-translate-y-1.5"
                >
                  <f.icon size={20} className="text-[#a277fa]" aria-hidden />
                  <h3 className="mt-5 font-display text-xl font-bold tracking-[-0.02em]">{f.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/60">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.15} className="lg:col-span-6">
          <div className="flex h-full flex-col justify-center rounded-[1.5rem] bg-[#201e32] p-6 text-[#f3f0e8] sm:p-10">
            <div className="mb-7 flex items-center justify-between">
              <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#a277fa]">
                One of many good beginnings
              </p>
              <span className="h-1.5 w-1.5 rounded-full bg-[#f4d35e]" />
            </div>
            <BrowserMock />
            <button
              data-testid="work-showcase-cta"
              onClick={() => scrollToSection("#contact")}
              className="group mt-8 flex w-fit items-center gap-2 text-sm font-semibold text-[#a277fa] transition-colors hover:text-[#f4d35e]"
            >
              Start yours
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
